import { LoaderCircle } from 'lucide-react';
import { forwardRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes } from 'react';

export type ButtonVariant = 'default' | 'plain' | 'primary' | 'text';
export type ButtonColor = 'default' | 'danger';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  color?: ButtonColor;
  compact?: boolean;
  iconOnly?: boolean;
  disabled?: boolean;
  loading?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  default:
    'default-button inline-flex min-h-9 cursor-pointer items-center justify-center gap-1.5 rounded-[10px] border px-3 no-underline',
  plain: '',
  primary: 'primary-button',
  text: 'text-button inline-flex min-h-9 cursor-pointer items-center justify-center gap-1.25 rounded-[10px] border px-2.5',
};

const colorClasses: Record<ButtonColor, string> = {
  default: '',
  danger:
    'text-[#e5636d]! hover:border-[#e5636d]/40! hover:bg-[#e5636d]/10! focus-visible:border-[#e5636d]/40! focus-visible:bg-[#e5636d]/10!',
};

export const buttonClassName = (
  variant: ButtonVariant = 'default',
  options: { color?: ButtonColor; compact?: boolean; iconOnly?: boolean; className?: string } = {},
): string =>
  [
    'app-button text-xs! font-normal!',
    variantClasses[variant],
    colorClasses[options.color ?? 'default'],
    options.compact ? 'compact' : '',
    options.iconOnly
      ? 'button-icon-only inline-flex size-9! min-h-9! min-w-9! max-h-9! max-w-9! shrink-0 aspect-square items-center justify-center rounded-[10px] p-0'
      : '',
    options.className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'default',
      color = 'default',
      compact = false,
      iconOnly = false,
      className = '',
      type = 'button',
      disabled = false,
      loading = false,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        {...props}
        className={buttonClassName(variant, { color, compact, iconOnly, className }) || undefined}
        type={type}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        data-loading={loading || undefined}
      >
        {children}
        {loading ? <LoaderCircle className="button-loading-icon spin" aria-hidden="true" /> : null}
      </button>
    );
  },
);

Button.displayName = 'Button';

export interface ButtonLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: ButtonVariant;
  color?: ButtonColor;
  compact?: boolean;
}

export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  ({ variant = 'default', color = 'default', compact = false, className = '', ...props }, ref) => (
    <a ref={ref} className={buttonClassName(variant, { color, compact, className })} {...props} />
  ),
);

ButtonLink.displayName = 'ButtonLink';
